/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/ExpandedContext.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-EXPANDEDCTX-001
 * PURPOSE: Compose the four semantic lanes into the expanded triage grid.
 * ROLE IN ARCHITECTURE: Frame replacing the legacy ReaderSignalStrip on the
 *   flag-ON reader path (the legacy strip stays for flag-OFF hosts).
 * MAIN EXPORTS: ExpandedContext, ExpandedContextProps, expandedGridClass,
 *   laneCellClass.
 * NON-RESPONSIBILITIES: Lane content (lane leaves), menu items (menus/).
 * NOTES FOR FUTURE AI: Responsive contract — compact(<480) COLLAPSES to one
 *   row of chips (CompactLaneChips), each opening its own lane in an anchored
 *   overlay; standard+wide(480-1199) is ONE COLUMN, one lane per row, and
 *   the chips wrap. A split reader is about 480px. Sharing that width across
 *   Class, Intel, and Filed clipped "General" to "Ge" and "Add tag" to
 *   "Add ta", so the controls could not be read. expanded(>=1200) keeps
 *   four lanes on one row. Lanes separate with gap (change #430), not
 *   spreadsheet borders. Workflow row (Phase 5) renders when the host
 *   passes allocationMembers. Expanded lane tracks are content-sized,
 *   never equal fractions — restoring grid-cols-4 there reopens change
 *   #325. Do not put the under-1200 tiers back on fractional columns.
 */
import { Fragment, type ReactNode } from 'react'

import type { AllocationMember } from '../../components/EmailAllocationRail'
import { CompanyContextDrawer } from '../../company-context'
import type { Email } from '../../hooks/useEmails'
import type { getSamKanbanApi } from '@quotech/kanban-host'

import { cn } from '../../../_internal/lib/utils'
import { ClassificationLane } from '../lanes/ClassificationLane'
import { CompactLaneChips } from '../lanes/CompactLaneChips'
import { CompanyLane } from '../lanes/CompanyLane'
import { FiledToLane } from '../lanes/FiledToLane'
import { IntelligenceLane } from '../lanes/IntelligenceLane'
import { useLaneChipSummaries } from '../lanes/laneSummary'
import { SignalsManager } from '../lanes/SignalsManager'
import { useExpandedLanesState } from '../lanes/useExpandedLanesState'
import { buildLaneMenuItems } from '../menus/laneMenuItems'
import { LaneContextMenu } from '../menus/LaneContextMenu'
import { isWorkflowLane, type ReaderTriageLaneId } from '../menus/menuScopes'
import { useContextMenuRouting } from '../menus/useContextMenuRouting'
import { placementNeedsReview, selectReaderPlacements } from '../model/placements'
import type { ReaderHeaderSurface } from '../model/types'
import type { ReaderHeaderDensity } from '../responsive/density'
import { WorkflowRow } from '../workflow/WorkflowRow'

export interface ExpandedContextProps {
  email: Email
  api: ReturnType<typeof getSamKanbanApi>
  orgId: number | null
  density: ReaderHeaderDensity
  onRefetch?: () => void
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  onFilterCompany?: () => void
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: () => void
  onPeekJob: (jobId: number) => void
  canRequestRule: boolean
  onOpenRuleDialog: () => void
  /** Optional host people list enables the workflow row (Phase 5 slot). */
  allocationMembers?: AllocationMember[]
  /** Telemetry surface for the workflow cells. */
  surface?: ReaderHeaderSurface
  /** Ping popover opener (host-owned) — powers Ping owner/watcher items. */
  onPing?: () => void
  hideSenderIdentity?: boolean
  hideJobLink?: boolean
  suppressDecisionAffordances?: boolean
  /** Scroll/focus the Review Queue rendered by the triage band. */
  onOpenReviewQueue?: () => void
}

/**
 * Plan lane order. One list drives both the grid and the collapsed chip row
 * so the two tiers cannot disagree about which group comes first.
 */
const LANE_ORDER: readonly ReaderTriageLaneId[] = [
  'company',
  'classification',
  'intelligence',
  'filedTo',
]

/** Pure: density tier → grid classes (exported for the tier tests). */
export function expandedGridClass(density: ReaderHeaderDensity): string {
  if (density === 'compact') {
    // Bottom-sheet tier: one column. Gap (not divide-y) — spreadsheet
    // hairlines read as form chrome against the chips (operator 2026-08-05).
    return 'flex flex-col gap-1'
  }
  // Two rules hold on every non-compact tier below.
  //
  // 1. Separation is gap, not per-cell border/divide (change #430). The old
  //    2×2 bordered table made short chips feel trapped in uneven boxes; gap
  //    keeps the no-nested-card rule without the spreadsheet look.
  //
  // 2. Because there are no borders, gap SIZE is the only thing telling the
  //    operator where COMPANY ends and CLASS begins — and it reads as a RATIO
  //    against the gap inside a lane (LaneShell's gap-1, 4px), never as an
  //    absolute. Anything under 3x and proximity makes the lanes read as one
  //    continuous run of chips. That sets the floor each tier spends from:
  //    gap-x-3 (3x) where width is scarce, gap-x-6 (6x) where it is not.
  //
  // Width itself is spent per tier, because the tiers do not have the same
  // problem: the four-lane row needs content-sized tracks (change #325 — equal
  // `1fr` quarters gave a one-chip lane the same width as a five-chip lane, so
  // most of the strip was gutter), while the two-row tiers need fixed weights
  // (see the starvation note below).
  if (density === 'standard' || density === 'wide') {
    // One lane per row. The split inbox reader is about 480px, which is this
    // tier. Sharing that width across Class, Intel, and Filed clipped the
    // words the operator has to read ("Ge", "Add ta", "Brava"). A full-width
    // row lets each chip keep its label and wrap onto the next line when the
    // filing breadcrumb is long. gap-3 is 3x LaneShell's inner gap-1, so the
    // rows stay separate without a border.
    return 'flex flex-col gap-3'
  }
  // expanded (>=1200): four lanes, one row — the only tier with the width for
  // it without clipping the trailing lane.
  return 'grid grid-cols-[auto_auto_auto_minmax(0,1fr)] gap-x-6'
}

/**
 * Pure: which grid cell a lane occupies at this tier.
 *
 * Only COMPANY ever asks for more than one track, and only on the two-row
 * tiers, where it owns the first row outright (see expandedGridClass). Every
 * other lane takes its natural single track, so this returns '' for them
 * rather than a no-op class — a lane with no cell class is the normal case.
 */
export function laneCellClass(
  lane: ReaderTriageLaneId,
  density: ReaderHeaderDensity,
): string {
  // Stacked tiers have one column, so no lane spans anything. The desktop
  // row sizes its tracks from content. `lane` and `density` stay in the
  // signature because the grid tests and callers already pass them.
  void lane
  void density
  return ''
}

/**
 * Assembles lanes, routing, and overlays for the expanded triage context.
 * Hooks + guards + JSX only — all logic lives in the lane leaves, the
 * routing hook, and the pure builders.
 */
export function ExpandedContext({
  email,
  api,
  orgId,
  density,
  onRefetch,
  onPatchEmail,
  onFilterCompany,
  onOpenCard,
  onOpenBoard,
  onPeekJob,
  canRequestRule,
  onOpenRuleDialog,
  allocationMembers,
  surface,
  onPing,
  hideSenderIdentity,
  hideJobLink,
  suppressDecisionAffordances,
  onOpenReviewQueue,
}: ExpandedContextProps) {
  const routing = useContextMenuRouting()
  const lanes = useExpandedLanesState({
    email,
    onFilterCompany,
    canRequestRule,
    onOpenRuleDialog,
  })

  const menuItems = routing.openMenu
    ? buildLaneMenuItems(routing.openMenu.scope, lanes.menuDeps)
    : []

  const collapsed = density === 'compact'
  // Under 1200px each lane is a full row. Wrapping is what keeps "General"
  // and "Add tag" readable. The desktop row has the width to stay one line.
  const wrapLane = density !== 'expanded'
  const summaries = useLaneChipSummaries(email, {
    hasVerification: Boolean(lanes.activeVerification),
    hasInvoice: Boolean(lanes.activeInvoice),
    reviewCount: selectReaderPlacements(email).filter(placementNeedsReview).length,
  })

  /**
   * The lane ELEMENTS, built once. The collapsed tier mounts these inside the
   * chip overlays and the other tiers mount them in the grid, so there is one
   * implementation of each lane and no narrow-width fork of its handlers.
   * `expand` only differs because a popover has height to spare.
   */
  const laneNodes: Record<ReaderTriageLaneId, ReactNode> = {
    company: (
      <CompanyLane
        email={lanes.identityEmail}
        className={laneCellClass('company', density)}
        surfaceProps={routing.laneSurfaceProps('company')}
        onOpenEllipsis={routing.openFromEllipsis}
        onRefetch={onRefetch}
        onOpenCompanyContext={() => lanes.setCompanyContextOpen(true)}
        onShowCompanyEmails={onFilterCompany}
        hideSenderIdentity={hideSenderIdentity}
        expand={wrapLane}
      />
    ),
    classification: (
      <ClassificationLane
        email={email}
        surfaceProps={routing.laneSurfaceProps('classification')}
        onOpenEllipsis={routing.openFromEllipsis}
        onRefetch={onRefetch}
        expand={wrapLane}
      />
    ),
    intelligence: (
      <IntelligenceLane
        email={email}
        surfaceProps={routing.laneSurfaceProps('intelligence')}
        onOpenEllipsis={routing.openFromEllipsis}
        verificationCode={lanes.activeVerification}
        invoiceNumber={lanes.activeInvoice}
        onDismissVerification={lanes.dismissVerification}
        onOpenSignalsManager={() => lanes.setSignalsManagerOpen(true)}
        expand={wrapLane}
      />
    ),
    filedTo: (
      <FiledToLane
        email={email}
        surfaceProps={routing.laneSurfaceProps('filedTo')}
        onOpenEllipsis={routing.openFromEllipsis}
        api={api}
        orgId={orgId}
        onRefetch={onRefetch}
        onPatchEmail={onPatchEmail}
        onOpenCard={onOpenCard}
        onOpenBoard={onOpenBoard}
        onPeekJob={onPeekJob}
        hideJobLink={hideJobLink}
        suppressDecisionAffordances={suppressDecisionAffordances}
        placementManagerOpen={lanes.placementManagerOpen}
        onPlacementManagerOpenChange={lanes.setPlacementManagerOpen}
        onOpenReviewQueue={onOpenReviewQueue}
        expand={wrapLane}
      />
    ),
  }

  return (
    <div
      data-testid="reader-expanded-context"
      data-expanded-density={density}
      className="min-w-0 max-w-full"
    >
      {collapsed ? (
        <CompactLaneChips
          summaries={summaries}
          laneNodes={laneNodes}
          // Every layer a lane can raise portals ABOVE the chip popover, so
          // each one would otherwise read as an outside press and dismiss the
          // panel it was opened from. See LaneChip.requestOpenChange.
          laneOverlayOpen={
            routing.openMenu !== null
            || lanes.companyContextOpen
            || lanes.signalsManagerOpen
            || lanes.placementManagerOpen
          }
        />
      ) : (
        <div className={cn('min-w-0', expandedGridClass(density))}>
          {LANE_ORDER.map((lane) => (
            <Fragment key={lane}>{laneNodes[lane]}</Fragment>
          ))}
        </div>
      )}
      {allocationMembers !== undefined && (
        /* Workflow row (Phase 5) — Owner/Due/Watchers cells with independent
         * busy keys; shares this context's ONE menu router. */
        <div
          data-testid="reader-workflow-slot"
          className="mt-1.5 border-t border-border/40 pt-1.5"
        >
          <WorkflowRow
            email={email}
            members={allocationMembers}
            routing={routing}
            surface={surface}
            onPatchEmail={onPatchEmail}
            onPing={onPing}
          />
        </div>
      )}
      {routing.openMenu && !isWorkflowLane(routing.openMenu.scope.lane) && (
        /* Workflow-lane menus render inside WorkflowRow (its deps). */
        <LaneContextMenu
          menu={routing.openMenu}
          items={menuItems}
          onClose={routing.closeMenu}
        />
      )}
      {lanes.identityEmail.company_domain && (
        <CompanyContextDrawer
          open={lanes.companyContextOpen}
          domain={lanes.identityEmail.company_domain}
          label={
            lanes.identityEmail.company_label ||
            lanes.identityEmail.company_domain
          }
          onClose={() => lanes.setCompanyContextOpen(false)}
          onShowEmails={onFilterCompany}
          onChanged={onRefetch}
        />
      )}
      <SignalsManager
        open={lanes.signalsManagerOpen}
        email={email}
        verificationCode={lanes.activeVerification}
        invoiceNumber={lanes.activeInvoice}
        onClose={() => lanes.setSignalsManagerOpen(false)}
        onRefetch={onRefetch}
      />
    </div>
  )
}
